import { Injectable } from '@angular/core';
import { NavigationEnd, NavigationError, Router } from '@angular/router';
import { BehaviorSubject, firstValueFrom } from 'rxjs';
import { SweetAlert2LoaderService } from '@sweetalert2/ngx-sweetalert2';
import { TranslateService } from '@ngx-translate/core';
import { BUILD_VERSION, compareBuildVersions } from '@core-shared/misc/build-version';

/** Response header in which the backoffice API sends its build number (#6388). */
export const API_BUILD_VERSION_HEADER = 'X-Auex-Version';

/**
 * Notices that the running backoffice is older than its API, and recovers from a lazy-loaded bundle
 * that a deployment removed (#6388).
 */
@Injectable()
export class UpdateService {

  /** Holds the target URL of a reload after a failed bundle load, so a second failure does not loop. */
  private static readonly chunkReloadKey = 'bo-chunk-reload';

  public readonly isUpdateAvailable$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  constructor(private swal: SweetAlert2LoaderService,
              private translate: TranslateService,
              router: Router) {
    router.events.subscribe((event) => {
      if (event instanceof NavigationEnd) {
        sessionStorage.removeItem(UpdateService.chunkReloadKey);
      }
      else if (event instanceof NavigationError && isChunkLoadError(event.error)) {
        void this.recoverFromChunkLoadError(event.url);
      }
    });
  }

  /**
   * Compares the build number the API sent with the one of this bundle. Only an older backoffice
   * gets the hint: a newer one appears for a moment during a rollout, and a reload would not help it.
   */
  public checkApiVersion(apiVersion: string | null): void {
    if (this.isUpdateAvailable$.value || !BUILD_VERSION || !apiVersion) {
      return;
    }

    if (compareBuildVersions(BUILD_VERSION, apiVersion) < 0) {
      this.isUpdateAvailable$.next(true);
      void this.showUpdateDialog();
    }
  }

  public async showUpdateDialog(): Promise<void> {
    const texts = await this.translations('update.available');
    const swal = await this.swal.swal;
    const result = await swal.fire({
      title: texts.title,
      text: texts.text,
      icon: 'info',
      confirmButtonText: texts.confirm,
      cancelButtonText: texts.later,
      showCancelButton: true
    });
    if (result.value) {
      location.reload();
    }
  }

  /**
   * The bundle of the target page belongs to a build that is no longer deployed. Loading the target
   * page anew fetches the current build. The page being left loses nothing a navigation would not
   * discard anyway; the live stream page keeps its beforeunload prompt.
   */
  private async recoverFromChunkLoadError(url: string): Promise<void> {
    if (sessionStorage.getItem(UpdateService.chunkReloadKey) === null) {
      sessionStorage.setItem(UpdateService.chunkReloadKey, url);
      location.assign(url);
      return;
    }

    sessionStorage.removeItem(UpdateService.chunkReloadKey);
    const texts = await this.translations('update.loadFailed');
    const swal = await this.swal.swal;
    await swal.fire({
      title: texts.title,
      text: texts.text,
      icon: 'error'
    });
  }

  /**
   * The first API responses arrive before the bootstrap guard has chosen a language, and until then
   * get() answers with the key itself. Wait for the language, then for its translations.
   */
  private async translations(key: string): Promise<Record<string, string>> {
    if (!this.translate.currentLang()) {
      await firstValueFrom(this.translate.onLangChange);
    }
    return firstValueFrom(this.translate.get(key));
  }

}

/** Messages of a failed dynamic import in Chromium, Firefox and Safari. */
function isChunkLoadError(error: unknown): boolean {
  const message = error instanceof Error ? error.message : String(error);
  return /Failed to fetch dynamically imported module|error loading dynamically imported module|Importing a module script failed/i.test(message);
}
